/**
 * Slice 53 — the rooms a cleaner walks: the owner's walk order, each room's reference photos, and everything
 * that belongs in it (unit items, supplies and appliances). Empty when the property has no rooms
 * (the old Walk is used then).
 */
import { useMemo } from "react";
import { usePropertyRooms } from "@/hooks/usePropertyRooms";
import { useRoomCoverData } from "@/hooks/useRoomCoverPhotos";
import { useUnitItems } from "@/hooks/useUnitItems";
import { usePhotoGroups } from "@/hooks/usePhotoGroups";
import { useInventoryForCleaner } from "@/hooks/useCleaner";
import { useInventoryRooms } from "@/hooks/useInventoryRooms";
import { useAppliances } from "@/hooks/useAppliances";
import { useReferencePhotos } from "@/hooks/useReferencePhotos";
import { buildRoomShots, groupsForRoom } from "@/lib/photoGroups";
import { sortRoomsWalkOrder } from "@/lib/roomCoverPhotos";
import { normalizeRoomName } from "@/lib/roomUtils";
import type { WalkRoom } from "@/lib/roomPhotos";

const MAX_REFS = 4;

export function useWalkRooms(propertyId: string | undefined) {
  const roomsQuery = usePropertyRooms(propertyId);
  const coverQuery = useRoomCoverData(propertyId);
  const itemsQuery = useUnitItems(propertyId);
  const groupsQuery = usePhotoGroups(propertyId);
  const supplyQuery = useInventoryForCleaner(propertyId);
  const invRoomsQuery = useInventoryRooms(propertyId);
  const applianceQuery = useAppliances(propertyId);
  const refQuery = useReferencePhotos(propertyId);

  const rooms = useMemo<WalkRoom[]>(() => {
    const raw = (roomsQuery.data ?? []).filter((r) => !r.archived_at);
    const cover = coverQuery.data ?? {};
    const ordered = sortRoomsWalkOrder(raw, cover);
    const units = itemsQuery.data ?? [];
    const groups = groupsQuery.data ?? [];
    const supplies = (supplyQuery.data ?? []) as {
      id: string;
      item_name?: string | null;
      photo_url?: string | null;
      room_id?: string | null;
      photo_group_id?: string | null;
    }[];
    const invRooms = invRoomsQuery.data ?? [];
    const appliances = (applianceQuery.data ?? []) as {
      id: string;
      name?: string | null;
      location?: string | null;
      photo_urls?: string[] | null;
      photo_group_id?: string | null;
    }[];
    const refList = refQuery.data?.list ?? [];

    return ordered.map((room) => {
      const n = normalizeRoomName(room.name);
      const invRoomIds = new Set(invRooms.filter((r) => normalizeRoomName(r.name) === n).map((r) => r.id));
      const roomItems: WalkRoom["items"] = [
        ...units
          .filter((it) => normalizeRoomName(it.room || "") === n)
          .map((it) => ({
            id: it.id,
            name: it.name,
            photoUrl: it.photo_url ?? null,
            quantity: it.quantity ?? null,
            groupId: it.photo_group_id ?? null,
            kind: "unit" as const,
          })),
        ...supplies
          .filter((it) => it.room_id && invRoomIds.has(it.room_id))
          .map((it) => ({
            id: it.id,
            name: it.item_name || "Supply",
            photoUrl: it.photo_url ?? null,
            quantity: null,
            groupId: it.photo_group_id ?? null,
            kind: "supply" as const,
          })),
        ...appliances
          .filter((a) => normalizeRoomName(a.location || "") === n)
          .map((a) => ({
            id: a.id,
            name: a.name || "Appliance",
            photoUrl: a.photo_urls?.[0] ?? null,
            quantity: null,
            groupId: a.photo_group_id ?? null,
            kind: "appliance" as const,
          })),
      ];
      const coverUrl = cover[room.id]?.cover_photo_url ?? null;
      const noCover = !!cover[room.id]?.cover_not_needed && !cover[room.id]?.cover_photo_url;
      const refUrls: string[] = [];
      if (coverUrl) refUrls.push(coverUrl);
      if (!noCover) {
        for (const ref of refList) {
          if (normalizeRoomName(ref.room_name || "") !== n) continue;
          const url = ref.photo_url?.trim();
          if (url && !refUrls.includes(url)) refUrls.push(url);
        }
      }
      return {
        id: room.id,
        name: room.name,
        coverUrl,
        refUrls: refUrls.slice(0, MAX_REFS),
        noCover,
        items: roomItems,
        shots: buildRoomShots({
          roomName: room.name,
          coverUrl: coverUrl ?? refUrls[0] ?? null,
          groups: groupsForRoom(groups, room.id),
          items: roomItems.map((it) => ({ id: it.id, name: it.name, photo_group_id: it.groupId })),
        }),
      };
    });
  }, [
    roomsQuery.data,
    coverQuery.data,
    itemsQuery.data,
    groupsQuery.data,
    supplyQuery.data,
    invRoomsQuery.data,
    applianceQuery.data,
    refQuery.data,
  ]);

  return { rooms, isLoading: roomsQuery.isLoading || coverQuery.isLoading || itemsQuery.isLoading };
}
